/**
 * 🔒 PROTECTED GUEST-FACING MODULE — DO NOT MODIFY WITHOUT EXPLICIT USER APPROVAL
 * ═══════════════════════════════════════════════════════════════════════════════
 * Locked under /app/memory/PROTECTED_GUEST_FACING.md (Feb 2026).
 * Read that file before proposing edits.
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * ExperiencePage — /experience/:token
 *
 * Dedicated invite flow. Bypasses all entry gates (maintenance, splash, etc.)
 * Wire:
 *  1. Fetch party + guests from /api/parties/by-token/:token
 *  2. Fetch video_url from /api/public/guest-site-config
 *  3. Show welcome screen (greeting + video + buttons)
 *  4. On video end OR "Skip to RSVP" → scroll to RSVP section
 *  5. RSVP already submitted → show submitted state (still allow video)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { RSVPCard } from '../components/RSVPPublicModule';
import { resolveGuestGreeting } from '../lib/resolveGuestGreeting';

const API = process.env.REACT_APP_BACKEND_URL;
const VIDEO_URL = 'https://customer-assets.emergentagent.com/job_96f73dc0-b292-41dd-a366-8b41de2ebdcc/artifacts/sj38t5q9_4.9.26%20Invitation%20Video.mp4';

// ─── Shared glass card (ivory) ────────────────────────────────────────────────
const glassCard = {
  background: 'rgba(255,252,240,0.62)',
  backdropFilter: 'blur(20px) saturate(1.40) brightness(1.05)',
  WebkitBackdropFilter: 'blur(20px) saturate(1.40) brightness(1.05)',
  border: '1px solid rgba(200,170,60,0.45)',
  borderRadius: 20,
  boxShadow: [
    'inset 0 1.5px 0 rgba(255,255,255,0.90)',
    'inset 0 -1px 0 rgba(200,170,60,0.22)',
    '0 12px 40px rgba(140,100,20,0.14)',
    '0 0 0 0.5px rgba(200,170,60,0.18)',
  ].join(', '),
};

const GoldDivider = () => (
  <div style={{ display:'flex', alignItems:'center', gap:8, justifyContent:'center' }}>
    <div style={{ flex:1, height:1, background:'linear-gradient(90deg,transparent,rgba(200,170,60,0.65))' }} />
    <span style={{ color:'rgba(200,170,60,0.90)', fontSize:10 }}>✦</span>
    <div style={{ flex:1, height:1, background:'linear-gradient(90deg,rgba(200,170,60,0.65),transparent)' }} />
  </div>
);

// ─── Loading state ─────────────────────────────────────────────────────────────
function LoadingScreen() {
  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      background:'#FDF9F3' }}>
      <p style={{ fontFamily:"'Cinzel',serif", fontSize:12, letterSpacing:'3px',
        textTransform:'uppercase', color:'rgba(140,100,20,0.70)' }}>
        Preparing your invitation…
      </p>
    </div>
  );
}

// ─── Not found state ──────────────────────────────────────────────────────────
function NotFoundScreen() {
  return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center',
      padding:24, textAlign:'center' }}>
      <div>
        <p style={{ fontFamily:"'Cinzel',serif", fontSize:11, letterSpacing:'3px',
          textTransform:'uppercase', color:'rgba(140,100,20,0.70)', marginBottom:12 }}>
          Invitation Not Found
        </p>
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:20, fontStyle:'italic',
          color:'#1E1A14' }}>
          This invitation link may be invalid or expired.<br/>
          Please contact the host directly.
        </p>
      </div>
    </div>
  );
}

// ─── Main Experience Page ─────────────────────────────────────────────────────
export default function ExperiencePage() {
  const { token }           = useParams();
  const videoRef            = useRef(null);
  const rsvpRef             = useRef(null);

  const [party, setParty]   = useState(null);
  const [guests, setGuests] = useState([]);
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading]   = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [videoPlaying, setVideoPlaying]   = useState(false);   // true only after onPlaying fires
  const [videoReady, setVideoReady]       = useState(false);
  const [videoLoading, setVideoLoading]   = useState(false);   // true between tap and onPlaying
  const [videoBuffering, setVideoBuffering] = useState(false); // true during mid-play stall

  // ── Fetch party + config ──────────────────────────────────────────────────
  useEffect(() => {
    const fetchParty = fetch(`${API}/api/parties/by-token/${token}`)
      .then(r => r.json())
      .then(d => {
        if (d.found && d.party) {
          setParty(d.party);
          setGuests(d.guests || []);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true));

    const fetchConfig = fetch(`${API}/api/public/guest-site-config`)
      .then(r => r.json())
      .then(d => {
        if (d?.video_url) {
          // video_url is relative (/api/static/...) — prepend backend base
          const url = d.video_url.startsWith('http') ? d.video_url : `${API}${d.video_url}`;
          setVideoUrl(url);
        }
      })
      .catch(() => {});

    Promise.all([fetchParty, fetchConfig]).finally(() => setLoading(false));
  }, [token]);

  // ── Name logic ────────────────────────────────────────────────────────────
  // Solo → guest-facing surfaces use first name only (e.g. "Evelyn"), per
  // Feb 2026 solo-greeting rule (admin picker keeps full name; guests are
  // greeted personally by first name on every guest-facing surface).
  // Multi → call shared resolver so picker (Compose Invite), generated message,
  // envelope, public RSVP greeting (InviteLandingPage), legacy /rsvp greeting
  // (RSVPPublicModule), AND this Experience screen all show the SAME string
  // for the SAME target.
  let displayName;
  if (guests.length === 1) {
    displayName = guests[0].first_name || 'Our Honored Guests';
  } else if (guests.length > 1) {
    const primary = guests.find(g => g.is_primary === true) || guests[0];
    displayName = resolveGuestGreeting(guests, party?.party_name, primary) || 'Our Honored Guests';
  } else {
    displayName = 'Our Honored Guests';
  }

  // ── Scroll to RSVP — pauses video if playing ("Reserve Your Place" transition) ──
  const scrollToRSVP = () => {
    if (videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
      setVideoPlaying(false);
      setVideoLoading(false);
      setVideoBuffering(false);
    }
    rsvpRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ── Video handlers ────────────────────────────────────────────────────────
  const handlePlay = () => {
    if (videoRef.current) {
      setVideoLoading(true);
      setVideoBuffering(false);
      videoRef.current.play().catch(() => setVideoLoading(false));
    }
  };

  const handleVideoEnded = () => {
    setVideoPlaying(false);
    setVideoLoading(false);
    setVideoBuffering(false);
    // Auto-transition to RSVP after brief pause
    setTimeout(scrollToRSVP, 800);
  };

  // ── Render guards ─────────────────────────────────────────────────────────
  if (loading)  return <LoadingScreen />;
  if (notFound) return <NotFoundScreen />;

  return (
    <div style={{
      minHeight: '100vh',
      backgroundImage: "url('https://customer-assets.emergentagent.com/job_b4805a07-765d-4b82-bd36-89754495be40/artifacts/wmrlspr0_new%20background%20for%20site%202.png')",
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      backgroundAttachment: 'fixed',
      fontFamily: "'Inter', -apple-system, sans-serif",
    }}>
      <style>{`@keyframes videoSpinner { to { transform: rotate(360deg); } }`}</style>

      {/* ══ SCREEN 1: Welcome ══════════════════════════════════════════════ */}
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '32px 16px',
        boxSizing: 'border-box',
      }}>
        <div style={{ ...glassCard, padding: '28px 22px 24px', width: '100%', maxWidth: 480 }}>

          {/* Greeting header */}
          <div style={{ textAlign:'center', marginBottom:20 }}>
            <GoldDivider />
            <p style={{ fontFamily:"'Cinzel',serif", fontSize:9, fontWeight:700,
              textTransform:'uppercase', letterSpacing:'3px',
              color:'rgba(140,100,20,0.75)', margin:'14px 0 10px' }}>
              An Invitation
            </p>
            <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:34,
              fontWeight:400, fontStyle:'italic', color:'#1E1A14',
              lineHeight:1.2, margin:'0 0 10px', letterSpacing:'0.5px',
              textShadow:'0 1px 0 rgba(255,255,255,0.85)' }}>
              {displayName}
            </h1>
            <p style={{ fontFamily:"'Cinzel',serif", fontSize:9, fontWeight:400,
              textTransform:'uppercase', letterSpacing:'2.5px',
              color:'rgba(140,100,20,0.75)', margin:'0 0 6px' }}>
              You are cordially invited
            </p>
            <p style={{ fontFamily:"'Cinzel',serif", fontSize:11, fontWeight:700,
              textTransform:'uppercase', letterSpacing:'1.5px',
              color:'#1E1A14', margin:'0 0 4px' }}>
              Carol's 80th Birthday Celebration
            </p>
            <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:15,
              color:'rgba(100,80,30,0.80)', margin:'0 0 16px', letterSpacing:'0.5px' }}>
              Saturday · July 25th · 2026
            </p>
            <GoldDivider />
          </div>

          {/* Video area */}
          <div style={{
            width:'100%', position:'relative',
            paddingBottom:'56.25%', /* 16:9 */
            borderRadius:14, overflow:'hidden',
            background:'#0D0B08',
            boxShadow:'0 8px 32px rgba(0,0,0,0.28), 0 0 0 1px rgba(200,170,60,0.30)',
            marginBottom:18,
          }}>
            {videoUrl ? (
              <>
                <video
                  ref={videoRef}
                  src={VIDEO_URL}
                  onEnded={handleVideoEnded}
                  onPlaying={() => { setVideoPlaying(true); setVideoLoading(false); setVideoBuffering(false); }}
                  onPause={() => { setVideoPlaying(false); setVideoLoading(false); setVideoBuffering(false); }}
                  onWaiting={() => setVideoBuffering(true)}
                  onCanPlay={() => setVideoReady(true)}
                  playsInline
                  preload="metadata"
                  style={{
                    position:'absolute', inset:0,
                    width:'100%', height:'100%',
                    objectFit:'cover',
                  }}
                />
                {/* Pre-play overlay: play button → loading spinner until onPlaying fires */}
                {!videoPlaying && (
                  <div
                    onClick={!videoLoading ? handlePlay : undefined}
                    style={{
                      position:'absolute', inset:0,
                      display:'flex', flexDirection:'column',
                      alignItems:'center', justifyContent:'center',
                      cursor: videoLoading ? 'default' : 'pointer',
                      background:'rgba(13,11,8,0.45)',
                    }}
                  >
                    {videoLoading ? (
                      <>
                        <div style={{
                          width:36, height:36,
                          border:'2px solid rgba(212,168,83,0.22)',
                          borderTopColor:'rgba(212,168,83,0.85)',
                          borderRadius:'50%',
                          animation:'videoSpinner 0.9s linear infinite',
                          marginBottom:10,
                        }} />
                        <p style={{ fontFamily:"'Cinzel',serif", fontSize:9, fontWeight:700,
                          letterSpacing:'2px', textTransform:'uppercase',
                          color:'rgba(212,168,83,0.85)', margin:0 }}>
                          Loading Film…
                        </p>
                      </>
                    ) : (
                      <>
                        <div style={{
                          width:60, height:60, borderRadius:'50%',
                          border:'1.5px solid rgba(212,168,83,0.65)',
                          display:'flex', alignItems:'center', justifyContent:'center',
                          boxShadow:'0 0 24px rgba(212,168,83,0.20)',
                          marginBottom:10,
                        }}>
                          <div style={{
                            width:0, height:0,
                            borderTop:'10px solid transparent',
                            borderBottom:'10px solid transparent',
                            borderLeft:'18px solid rgba(212,168,83,0.85)',
                            marginLeft:4,
                          }} />
                        </div>
                        <p style={{ fontFamily:"'Cinzel',serif", fontSize:9, fontWeight:700,
                          letterSpacing:'2px', textTransform:'uppercase',
                          color:'rgba(212,168,83,0.85)', margin:0 }}>
                          Play Film
                        </p>
                      </>
                    )}
                  </div>
                )}
                {/* Mid-play buffering indicator */}
                {videoPlaying && videoBuffering && (
                  <div style={{
                    position:'absolute', inset:0,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    background:'rgba(0,0,0,0.28)',
                    pointerEvents:'none',
                  }}>
                    <div style={{
                      width:32, height:32,
                      border:'2px solid rgba(212,168,83,0.22)',
                      borderTopColor:'rgba(212,168,83,0.85)',
                      borderRadius:'50%',
                      animation:'videoSpinner 0.9s linear infinite',
                    }} />
                  </div>
                )}
              </>
            ) : (
              /* No video yet — placeholder */
              <div style={{
                position:'absolute', inset:0,
                display:'flex', flexDirection:'column',
                alignItems:'center', justifyContent:'center', gap:8,
              }}>
                <div style={{
                  width:56, height:56, borderRadius:'50%',
                  border:'1.5px solid rgba(212,168,83,0.40)',
                  display:'flex', alignItems:'center', justifyContent:'center',
                }}>
                  <div style={{
                    width:0, height:0,
                    borderTop:'9px solid transparent',
                    borderBottom:'9px solid transparent',
                    borderLeft:'16px solid rgba(212,168,83,0.50)',
                    marginLeft:3,
                  }} />
                </div>
                <p style={{ fontFamily:"'Cinzel',serif", fontSize:8, letterSpacing:'2px',
                  textTransform:'uppercase', color:'rgba(212,168,83,0.50)', margin:0 }}>
                  Film coming soon
                </p>
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
            {videoUrl && !videoPlaying && (
              <button
                onClick={!videoLoading ? handlePlay : undefined}
                style={{
                  width:'100%', height:52, borderRadius:12, border:'none',
                  cursor: videoLoading ? 'default' : 'pointer',
                  background:'linear-gradient(145deg,#D4AA50 0%,#C49830 40%,#B48820 70%,#C49830 100%)',
                  boxShadow:'0 4px 14px rgba(180,130,20,0.34), inset 0 1.5px 0 rgba(255,235,130,0.40)',
                  fontFamily:"'Cinzel',serif", fontSize:11, fontWeight:700,
                  color:'#FFF5D0', letterSpacing:'2px', textTransform:'uppercase',
                  opacity: videoLoading ? 0.7 : 1,
                }}
              >
                {videoLoading ? 'Loading…' : 'Play Film'}
              </button>
            )}
            <button
              onClick={scrollToRSVP}
              style={{
                width:'100%', height:48, borderRadius:12, cursor:'pointer',
                background:'rgba(255,252,240,0.30)',
                border:'1px solid rgba(200,170,60,0.50)',
                fontFamily:"'Cinzel',serif", fontSize:10, fontWeight:600,
                color:'rgba(100,80,20,0.90)', letterSpacing:'2px', textTransform:'uppercase',
              }}
            >
              Skip to RSVP
            </button>
          </div>

        </div>
      </div>

      {/* ══ SCREEN 2: RSVP ═════════════════════════════════════════════════ */}
      <div ref={rsvpRef} style={{ padding:'0 16px 48px', maxWidth:480, margin:'0 auto' }}>

        {/* Dark glass card — matches RSVPPublicModule style */}
        <div style={{
          borderRadius:20, position:'relative',
          background:'rgba(20,15,8,0.58)',
          backdropFilter:'blur(18px) saturate(1.20)',
          WebkitBackdropFilter:'blur(18px) saturate(1.20)',
          border:'1.5px solid rgba(255,255,255,0.18)',
          boxShadow:[
            'inset 0 2px 0 rgba(255,255,255,0.14)',
            'inset 0 -1px 0 rgba(212,168,83,0.30)',
            '0 32px 64px rgba(0,0,0,0.30)',
          ].join(', '),
        }}>
          <div style={{ position:'relative', zIndex:2, padding:'32px 24px 28px' }}>
            {/* Full-featured RSVPCard — GuestCards, dietary, optional note/question */}
            <RSVPCard party={party} guests={guests} token={token} />
          </div>
        </div>

        {/* Footer note */}
        <p style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:13, fontStyle:'italic',
          color:'rgba(100,80,30,0.70)', textAlign:'center', marginTop:20, lineHeight:1.6 }}>
          For questions, contact the host directly.<br/>
          <span style={{ color:'rgba(140,100,20,0.75)' }}>Season's Catering · July 25th, 2026</span>
        </p>

      </div>
    </div>
  );
}
